import {inject, Injectable, signal, computed, DestroyRef} from '@angular/core';
import {CommunicationService} from '../http/communication.service';
import {ChatMessage, ChatMessageAuthor, PolarisOrigin} from '../components/copilot/copilot.model';
import {HistoryMessage, CopilotMessagePayload, CopilotMessageResponse, Wizard, WizardPayload, AlertPayload, WidgetContext} from '../components/copilot/types';
import {StateService} from './state.service';
import {catchError, Observable, timeout} from 'rxjs';
import {PolarisRoutePaths, RoutePaths} from '../shared/types/common';
import {Router} from '@angular/router';
import {AuthorizationService} from './authorization.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {subtractDays} from '../utils/time';
import {Signal} from '../components/signals-screen/types';
import {PosthogService} from './posthog.service';
import {PosthogEventName, PosthogEventCode} from './types';


@Injectable({
  providedIn: 'root',
})

export class CopilotService {
  private authorizationService = inject(AuthorizationService);
  private communicationService = inject(CommunicationService);
  private destroyRef = inject(DestroyRef);
  private stateService = inject(StateService);
  private router = inject(Router);
  private posthogService = inject(PosthogService);
  private _chats = signal<Record<string, Chat>>({});
  public myBrand = signal<string | null>(null);
  public myCompetitors = signal<string[]>([]);
  public lastAssistantMessageId = signal<string | null>(null);
  public customerName = '';
  public chats = computed(() => this._chats());

  private readonly STORAGE_KEY = 'copilot_chats';

  constructor() {
    this.loadChatsFromStorage();
    this.authorizationService.userProfile$.pipe(takeUntilDestroyed(this.destroyRef),).subscribe(userProfile => {
      this.customerName = userProfile.firstName;
    });
  }

  public getNewChat(avoidGreeting = false): Chat {
    const newChat = new Chat(avoidGreeting);
    newChat.myBrand = this.myBrand() ?? undefined;
    newChat.myCompetitors = [...this.myCompetitors()];
    const chats = this._chats();
    const updatedChats = { ...chats, [newChat.id]: newChat };
    this._chats.set(updatedChats);
    !avoidGreeting && this.addCopilotMessage(newChat.id, Chat.initialCopilotMessage);
    return newChat;
  }

  public getChat(chatId?: string, message?: string): Chat | void {
    const chats = this._chats();
    if (chatId && chats[chatId]) {
      return chats[chatId];
    }
    return undefined;
  }

  public getChatById(chatId: string): Chat | undefined {
    const chats = this._chats();
    return chats[chatId];
  }

  public ensureChatLoaded(chatId: string): boolean {
    const chats = this._chats();
    if (chats[chatId]) {
      return true;
    }

    this.loadChatsFromStorage();
    const updatedChats = this._chats();
    return !!updatedChats[chatId];
  }

  public getChatMessages(chatId: string) {
    return computed(() => {
      const chats = this._chats();
      return chats[chatId]?.messages || [];
    });
  }

  public addUserMessage(chatId: string, content: string, wizard?: WizardPayload, alert?: AlertPayload, signal?: Signal, origin?: PolarisOrigin, widgetContext?: WidgetContext): void {
    const chats = this._chats();
    const chat = chats[chatId];
    if (!chat || chat.isCopilotThinking()) {
      return;
    }
    const hasExistingWizard = !!chat.wizard;

    if (wizard || alert || signal || widgetContext) {
      chat.wizard = wizard;
      chat.alert = alert;
      chat.signal = signal;
      chat.widgetContext = widgetContext;
      this._chats.set({ ...chats });
    }
    let userPrompt = content;
    if (hasExistingWizard && wizard) {
      const parts = content.split('\n\n');
      userPrompt = parts.length > 1 ? parts[parts.length - 1] : content;
    }

    const message: ChatMessage = {
      id: crypto.randomUUID(),
      content,
      author: ChatMessageAuthor.USER,
      timestamp: Date.now(),
      interactionId: crypto.randomUUID(),
      origin,
      widgetName: widgetContext?.name,
    };
    chat.messages.push(message);
    chat.lastUpdate = Date.now();
    this._chats.set({ ...chats });
    if (chat.title === 'New Chat' && chat.messages.length === 1) {
      chat.title = this.generateChatTitle(content);
    }

    const hiddenPrompt = (chat.wizard || chat.alert) ? '' : this.getHiddenPrompt(chat);
    this.sendMessage(chat, hiddenPrompt + userPrompt, message);

    this.posthogService.capture(PosthogEventName.POLARIS_PROMPT_SENT, {
      [PosthogEventCode.MY_BRAND]: chat.myBrand ?? '',
      [PosthogEventCode.MY_COMPETITORS]: chat.myCompetitors?.join(';') ?? '',
      [PosthogEventCode.ORIGIN]: chat.origin ?? 'polaris_homepage',
      [PosthogEventCode.PROMPT_CONTENT]: userPrompt
    });
  }

  public retryUserMessage(chatId: string, interactionId?: string): void {
    const chats = this._chats();
    const chat = chats[chatId];
    if (!chat || chat.isCopilotThinking()) {
      return;
    }
    const userMessage = chat.messages
      .find(msg => msg.author === ChatMessageAuthor.USER && msg.interactionId === interactionId);
    if (!userMessage) {
      return;
    }
    this.addUserMessage(chatId, userMessage.content);
  }

  public replaceCreativesInMessage(message: string, creativesURLs: string[]): string {
    let newMessage = message;
    let currentLetter = 'A';
    creativesURLs.forEach(url => {
      const urlSplit = url.split('/');
      newMessage = newMessage.replace(urlSplit[urlSplit.length - 1], currentLetter);
      currentLetter = String.fromCharCode(currentLetter.charCodeAt(0) + 1);
    });
    return newMessage;
  }

  public triggerMessage(message: string, options?: { wizard?: WizardPayload; alert?: AlertPayload; signal?: Signal; origin?: PolarisOrigin; widgetContext?:WidgetContext }): void {
    const { wizard, alert, signal, origin, widgetContext } = options || {};
    const chat = this.getNewChat(true);
    chat.widgetName = widgetContext?.name;
    chat.origin = origin;
    if (wizard) {
      chat.myBrand = wizard.brands?.[0];
      chat.myCompetitors = wizard.competitors;
    }
    if (alert) {
      chat.myBrand = alert.brand_key;
    }
    this._chats.set({ ...this._chats() });
    this.addUserMessage(chat.id, message, wizard, alert, signal, origin, widgetContext );
    this.navigate(`${PolarisRoutePaths.CHAT}/${chat.id}`);
  }

  public navigate(route: string): void {
    this.router.navigate([`${RoutePaths.POLARIS}/${route}`]);
  }

  public getWizardsResponse(): Observable<Wizard[]> {
    return this.communicationService.httpGet<Wizard[]>('/polaris/wizards-config');
  }

  private generateChatTitle(message: string, maxLength = 20): string {
    return message.split(' ').slice(0, maxLength).join(' ');
  }

  private async sendMessage(chat: Chat, content: string, userMessage: ChatMessage): Promise<void> {
    chat.isCopilotThinking.set(true);
    const payload = this.getMessagePayload(chat.id, content);
    const copilotMessage = this.addCopilotMessage(chat.id, '', userMessage.interactionId);
    if (!copilotMessage) {
      return;
    }
    this.sendMessageRequest(payload).subscribe({
      next: response => {
        copilotMessage.content = response.answer.text;
        copilotMessage.insights = response.answer.insights;
        copilotMessage.graphs = response.graph;
        copilotMessage.creativesURLs = response.creativesURLs;
        if (response.lambdaId) {
          console.log("lambda request id:", response.lambdaId);
        }
        if (response.originalResponse) {
          console.log(response.originalResponse);
        }
        copilotMessage.followUpQuestions=response.followUpQuestions;
        chat.lastUpdate = Date.now();
        this.lastAssistantMessageId.set(copilotMessage.id);
        this.saveChatsToStorage();
      },
      error: error => {
        copilotMessage.content = "Something went wrong.";
        copilotMessage.error = 'Failed to generate response';
      }
    })
    chat.isCopilotThinking.set(false);
  }

  private addCopilotMessage(chatId: string, content?: string, interactionId?: string): ChatMessage | undefined {
    const chats = this._chats();
    if (chats[chatId]) {
      const message: ChatMessage = {
        id: crypto.randomUUID(),
        content: content ?? '',
        author: ChatMessageAuthor.ASSISTANT,
        timestamp: Date.now(),
        interactionId
      };

      chats[chatId].messages.push(message);
      chats[chatId].lastUpdate = Date.now();
      this._chats.set({...chats});
      return message;
    }
    return undefined;
  }

  private sendMessageRequest(payload: CopilotMessagePayload): Observable<CopilotMessageResponse> {
    return this.communicationService.httpPost<CopilotMessageResponse>('/polaris/send-message', payload).pipe(
      timeout(600000),
      catchError((error) => {
          console.error('Error fetching copilot response', error);
          throw error;
        }
      ));
  }

  private getConversationHistory(messages: ChatMessage[]): HistoryMessage[] {
    if (messages.length === 0) return [];

    // Exclude the current user prompt (the last message)
    const pastMessages = messages.slice(0, -1);

    // Get all interaction ids that failed
    const failedIds = pastMessages
      .filter(msg => msg.error)
      .map(msg => msg.interactionId);

    // Filter out messages with failed interaction ids
    return pastMessages
      .filter(msg => !failedIds.includes(msg.interactionId))
      .map(msg => ({role: msg.author, content: msg.content}));
  }

  private getMessagePayload(chatId: string, message: string): CopilotMessagePayload {
    const chats = this._chats();
    const chat = chats[chatId];
    return {
      requestId: chatId + chat.messages.length,
      userPrompt: message,
      sessionContext: {
        sessionId: chatId,
        customerName: this.customerName,
        customerBrandsList: []
      },
      workflowContext: '',
      conversationHistory: this.getConversationHistory(chat.messages),
      agencyId: this.stateService.activeAccount(),
      wizard: chat.wizard,
      alert: chat.alert,
      signal: chat.signal,
      widgetContext: chat.widgetContext
    }
  }

  private loadChatsFromStorage(): void {
    try {
      const storedChats = localStorage.getItem(this.STORAGE_KEY);
      if (storedChats) {
        const parsedChats = JSON.parse(storedChats);
        const restoredChats: Record<string, Chat> = {};
        Object.keys(parsedChats).forEach(chatId => {
          const chatData = parsedChats[chatId];
          const chat = new Chat(chatData.avoidGreeting);
          chat.id = chatData.id;
          chat.messages = chatData.messages;
          chat.title = chatData.title;
          chat.lastUpdate = chatData.lastUpdate;
          chat.myBrand = chatData.myBrand;
          chat.myCompetitors = chatData.myCompetitors;
          chat.wizard = chatData.wizard;
          chat.origin = chatData.origin;
          restoredChats[chatId] = chat;
        });
        this._chats.set(restoredChats);
      }
    } catch (error) {
      console.error('Error loading chats from localStorage:', error);
      this._chats.set({});
    }
  }

  public saveChatsToStorage(): void {
    try {
      const chats = this._chats();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(chats));
    } catch (error) {
      console.error('Error saving chats to localStorage:', error);
    }
  }

  private getHiddenPrompt(chat: Chat): string {
    const hiddenPromptDays = 30;
    let hiddenPrompt = '';
    if (chat.myBrand) {
      hiddenPrompt += `My brand key is ${chat.myBrand}. `;
    }
    if (chat.myCompetitors && chat.myCompetitors.length > 0) {
      hiddenPrompt += `My competitors are ${chat.myCompetitors?.join(',')}. `;
    }
    const dateRangeString = `${subtractDays(new Date(),hiddenPromptDays).toISOString().split('T')[0]} - ${new Date().toISOString().split('T')[0]}`;
    hiddenPrompt += `Unless I specify another time frame later in this message, the time frame I'm interested in for your answers is ${dateRangeString}.\n`;
    return hiddenPrompt;
  }

}

class Chat {
  id: string;
  messages: ChatMessage[] = [];
  static initialCopilotMessage = 'Hi, how can I help you?';
  isCopilotThinking = signal(false);
  title = 'New Chat';
  lastUpdate = Date.now();
  avoidGreeting?: boolean;
  myBrand?: string;
  myCompetitors?: string[];
  wizard?: WizardPayload;
  alert?: AlertPayload;
  signal?: Signal;
  origin?: PolarisOrigin;
  widgetName?: string;
  widgetContext?: WidgetContext;

  constructor(avoidGreeting?: boolean) {
    this.id = crypto.randomUUID();
    this.avoidGreeting = avoidGreeting;
  }
}
